import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from "rxjs";

import { StorageService } from "../../shared/storage/storage.service";
import { capitalize } from '../../shared/tool-functions/capitalize';
import { PhoneCode } from '../../shared/tool-functions/phone.number';
import { EntityFormGroup } from "../../shared/types/entity-form-group";
import { PhoneNumberValidatorFactory } from '../../shared/validators/phoneNumberValidator';
import { GenderDescription } from '../../users/models/users.entity';
import { AuthenticationService } from '../services/authentication.service';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: [ './signup.component.scss' ]
})
export class SignupComponent implements OnInit, OnDestroy {
  formError = false;

  loginForm: EntityFormGroup<{ email: string, firstname: string, lastname: string, gender: string, accountType: string, companyName: string, phoneNumber: string }>;

  errorMessage: string = '';

  returnUrl!: string;

  apiCalling = false;

  showSubscribeError = false;

  options = ["Un particulier / auto-entrepreneur", "Une entreprise"];

  phoneRegionCode: PhoneCode = {
    code: 33,
    region: 'FR'
  };

  isInvalid = false;

  formState: 'idle' | 'submitting' | 'success' = 'idle';

  successMessage = '';

  submittedAccountType: string | null = null;

  showEmailAlreadyExistsError = false;

  genderPossibilities = Object.values(GenderDescription).map((gender) =>
    capitalize(gender),
  );

  private destroy$ = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly authenticationService: AuthenticationService,
    private readonly titleService: Title,
    private readonly storageService: StorageService
  ) {
    this.titleService.setTitle('Connection');
    this.loginForm = this.formBuilder.group({
        email: new FormControl('', { 
          nonNullable: true, 
          validators: [Validators.required, Validators.email] 
        }),
        firstname: new FormControl('', {
          validators: [Validators.required, Validators.minLength(1)], 
          nonNullable: true,
        }),
        lastname: new FormControl('', {
          validators: [Validators.required, Validators.minLength(1)],
          nonNullable: true,
        }),
        gender: new FormControl('', {
          validators: Validators.required,
          nonNullable: true,
        }),
        accountType: new FormControl('', {
          validators: Validators.required,
          nonNullable: true,
        }),
        companyName: new FormControl('', {
          nonNullable: true,
        }),
        phoneNumber: new FormControl('', { 
          nonNullable: true, 
          validators: [Validators.required, Validators.pattern(/^\s*\S.*$/)] 
        }),
      });
  }

  ngOnInit(){
    const { queryParams } = this.route.snapshot;
    this.storageService.setMode('session');
    this.returnUrl = queryParams?.returnUrl ?? this.storageService.getReturnUrl() ?? '/';
    
    this.loginForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((formValues) => {
        if (this.formError) {
          this.formError = false;
          this.setErrorMessage();
        }
        
        const currentAccountType = formValues.accountType;
    
        const companyNameControl = this.loginForm.get('companyName');
    
        if (currentAccountType === 'Une entreprise') {
          companyNameControl?.setValidators([Validators.required, Validators.minLength(2)]);
        } else {
          companyNameControl?.clearValidators();
        }
    
        companyNameControl?.updateValueAndValidity();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  validate(): void {
    if (this.loginForm.valid) {
      this.showEmailAlreadyExistsError = false;
      this.formState = 'submitting';
      const accountType = this.loginForm.controls.accountType.value;
      this.submittedAccountType = accountType;
      const formData = {
        email: this.loginForm.controls.email.value.toLowerCase(),
        firstName: this.loginForm.controls.firstname.value,
        lastName: this.loginForm.controls.lastname.value,
        gender: this.transformGender(this.loginForm.controls.gender.value),
        accountType,
        companyName: this.isCompany() ? this.loginForm.controls.companyName.value : null,
        phoneNumber: this.fullPhoneNumber
      };
      
      this.authenticationService.signup(formData)
        .pipe(
          takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.formState = 'success';
            this.formError = false;
            
            this.successMessage = accountType === 'Une entreprise' 
              ? 'Votre compte sera créé dans les 48H ouvrées, vous recevrez un email dès qu\'il sera prêt.' 
              : 'Rendez vous dans votre boîte mail, vous avez reçu un mail pour créer votre mot de passe !';
            
            setTimeout(() => {
              this.loginForm.reset();
              const companyNameControl = this.loginForm.get('companyName');
              companyNameControl?.clearValidators();
              companyNameControl?.updateValueAndValidity();
              
              setTimeout(() => {
                this.showEmailAlreadyExistsError = false;
                this.formState = 'idle';
              }, 3000);
            }, 5000);
          },
          error: (error) => {
            if(error.error.message === 'email_already_exists') {
              this.showEmailAlreadyExistsError = true;
              this.formState = 'idle';
              return;
            } 
            this.setErrorMessage();
            this.formState = 'idle';
          }
        });
    } else {
      this.isInvalid = true;
    }
  }

  setErrorMessage(): void {
    this.errorMessage = 'Une erreur s\'est produite lors de l\'inscription. Veuillez réessayer ultérieurement.';
}

  sendEmail() {
    window.location.href = 'mailto:contact@wetalkgroup.com';
  }

  isCompany(): boolean {
    return this.loginForm.get('accountType')?.value === 'Une entreprise';
  }

  selectPhoneRegionCode(code: PhoneCode) {
    this.phoneRegionCode = code;
  }

  handleForgotPasswordClick(){
    (window._mtm || []).push(
      { 
        'event': 'clic-mot-passe'
      }
    );
  }


  transformGender(gender: string): string {
    switch (gender) {
      case 'Homme':
        return 'man';
      case 'Femme':
        return 'woman';
      case 'Autre':
        return 'other';
      default:
        return gender.toLowerCase();
    }
  }

  get fullPhoneNumber(): string {
    return `+${ this.phoneRegionCode.code }${ this.loginForm.controls.phoneNumber?.value }`;
  }

  get phoneWork(): boolean {
      const regionValidator = PhoneNumberValidatorFactory(this.phoneRegionCode.region);
      return !regionValidator(this.fullPhoneNumber);
  }

  get buttonMessage(): string {
    switch (this.formState) {
      case 'submitting':
        return 'Envoi en cours...';
      case 'success':
        return this.submittedAccountType === 'Une entreprise' 
        ? 'Demande effectuée !' 
        : 'Envoyé !';
      default:
        return 'S\'inscrire';
    }
  }
  
}
